// Result view for the optimiser. Summary tiles + a horizontal
// stacked bar per stock bin showing each cut as a coloured segment
// and the drop as an amber tail.
import type { OptimizerResult } from "../lib/cutOptimizer";

interface Props {
  result: OptimizerResult;
  stockLength: number;
}

function effColour(pct: number): string {
  if (pct > 85) return "var(--bsp-green-fg)";
  if (pct > 70) return "var(--bsp-orange-fg)";
  return "var(--bsp-red-fg)";
}

export function CutOptimizerResult({ result, stockLength }: Props) {
  return (
    <>
      <div style={{ background: "var(--bsp-blue-pale)", border: "1px solid var(--bsp-blue-border)", borderRadius: 4, padding: 14, marginBottom: 14 }}>
        <div style={{ fontFamily: "var(--bsp-font-display)", fontSize: 11, fontWeight: 700, color: "var(--bsp-blue)", textTransform: "uppercase", letterSpacing: "0.10em", marginBottom: 8 }}>
          Optimisation Result
        </div>
        <div style={{ display: "grid", gridTemplateColumns: "repeat(4,1fr)", gap: 14, fontFamily: "var(--bsp-font-mono)", fontSize: 11 }}>
          <Stat label="Stock Bars" value={String(result.bins.length)} color="var(--bsp-blue)" />
          <Stat label="Cuts" value={String(result.totalCuts)} color="var(--bsp-dark)" />
          <Stat label="Material Used" value={`${(result.totalLength / 1000).toFixed(2)}m`} color="var(--bsp-dark)" />
          <Stat label="Efficiency" value={`${result.efficiencyPct}%`} color={effColour(result.efficiencyPct)} />
        </div>
      </div>
      <div>
        {result.bins.map((bin, i) => (
          <div key={i} style={{ background: "var(--bsp-surface)", border: "1px solid var(--bsp-border)", borderRadius: 4, padding: "10px 14px", marginBottom: 6 }}>
            <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 6 }}>
              <div style={{ fontFamily: "var(--bsp-font-mono)", fontWeight: 700, fontSize: 12 }}>
                Bar {i + 1} — {stockLength}mm
              </div>
              <div style={{ fontFamily: "var(--bsp-font-mono)", fontSize: 11, color: "var(--bsp-fg-muted)" }}>
                Used {Math.round(bin.used)}mm · Drop {Math.round(bin.remaining)}mm
              </div>
            </div>
            <div style={{ display: "flex", gap: 2, height: 22, background: "var(--bsp-surface-muted)", border: "1px solid var(--bsp-border)", borderRadius: 2, overflow: "hidden" }}>
              {bin.cuts.map((c, ci) => {
                const w = (c.length / stockLength) * 100;
                return (
                  <div
                    key={ci}
                    style={{
                      width: `${w}%`,
                      background: "var(--bsp-blue)",
                      color: "#fff",
                      fontSize: 9,
                      fontFamily: "var(--bsp-font-mono)",
                      fontWeight: 600,
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      borderRight: ci < bin.cuts.length - 1 ? "2px solid #fff" : "none",
                      overflow: "hidden",
                      whiteSpace: "nowrap",
                    }}
                    title={`${c.label} · ${c.length}mm`}
                  >
                    {c.length}
                  </div>
                );
              })}
              {bin.remaining > 0 && (
                <div
                  style={{
                    width: `${(bin.remaining / stockLength) * 100}%`,
                    background: "rgba(199,119,0,0.20)",
                    color: "var(--bsp-orange-fg)",
                    fontSize: 9,
                    fontFamily: "var(--bsp-font-mono)",
                    fontWeight: 600,
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                  }}
                >
                  {Math.round(bin.remaining)}
                </div>
              )}
            </div>
          </div>
        ))}
      </div>
    </>
  );
}

function Stat({ label, value, color }: { label: string; value: string; color: string }) {
  return (
    <div>
      <div style={{ color: "var(--bsp-fg-muted)", fontSize: 10, textTransform: "uppercase", letterSpacing: "0.08em" }}>{label}</div>
      <div style={{ fontSize: 18, fontWeight: 700, color }}>{value}</div>
    </div>
  );
}
